import React, { useContext, useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getData, getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import { LoadingModalContext } from '../../components/modal/loading';
import { useSnackbar } from 'notistack';
import { Formatter } from '../../const/formatter';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: 600,
    },
    dataRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        flexWrap: 'wrap',
    },
    dataLabel: {
        fontWeight: 500,
        color: theme.palette.text.secondary,
        minWidth: 140,
    },
    tablePaper: {
        overflow: 'auto',
    },
    emptyOrders: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    toolbar: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
}));

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [paciente, setPaciente] = useState(null);
    const [ordenes, setOrdenes] = useState([]);

    const idEdit = globalsContext.idEdit;
    const token = usuario?.token;

    const loadData = useCallback(async () => {
        if (!idEdit || !token) return;
        loadingModal.setOpen(true);
        try {
            const [resPaciente, resOrdenes] = await Promise.all([
                getData(`pacientes/${idEdit}`, token),
                getDataFull(`ordenes-practicas?per_page=200&selected_filter=${idEdit}&order_by=fecha&order=desc`, token),
            ]);
            if (resPaciente.error === '') setPaciente(resPaciente.data);
            else enqueueSnackbar(resPaciente.error, { variant: 'error' });
            if (resOrdenes.error === '' && resOrdenes.data) setOrdenes(resOrdenes.data);
            else setOrdenes([]);
        } catch (error) {
            enqueueSnackbar('Error al cargar los datos', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    }, [idEdit, token]);

    useEffect(() => {
        if (idEdit) loadData();
    }, [idEdit, loadData]);

    const onVolver = () => navigate(-1);
    const onEditar = () => navigate('/paciente/editar');

    if (!idEdit) {
        return (
            <Layout nombrePagina="Ver paciente">
                <Typography sx={{
                    color: "text.secondary"
                }}>No se ha seleccionado ningún paciente.</Typography>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/paciente')} sx={{ mt: 2 }}>
                    Volver al listado
                </Button>
            </Layout>
        );
    }

    return (
        <Layout nombrePagina={paciente ? `Paciente: ${paciente.nombre}` : 'Ver paciente'}>
            <Fade in={true} timeout={400}>
                <Grid size={12}>
                    <Box className={classes.toolbar}>
                        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onVolver} variant="outlined">
                            Volver
                        </Button>
                        {paciente && (
                            <Button startIcon={<EditRoundedIcon />} onClick={onEditar} variant="contained" color="primary">
                                Editar paciente
                            </Button>
                        )}
                    </Box>

                    {paciente && (
                        <Paper className={classes.paper}>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Datos del paciente
                            </Typography>
                            <div className={classes.dataRow}>
                                <Typography className={classes.dataLabel}>Nombre:</Typography>
                                <Typography>{paciente.nombre || '—'}</Typography>
                            </div>
                            <div className={classes.dataRow}>
                                <Typography className={classes.dataLabel}>Fecha de nacimiento:</Typography>
                                <Typography>{paciente.fecha_nacimiento ? Formatter.dateBdToUi(paciente.fecha_nacimiento) : '—'}</Typography>
                            </div>
                            <div className={classes.dataRow}>
                                <Typography className={classes.dataLabel}>DNI:</Typography>
                                <Typography>{paciente.dni || '—'}</Typography>
                            </div>
                            <div className={classes.dataRow}>
                                <Typography className={classes.dataLabel}>Domicilio:</Typography>
                                <Typography>{paciente.texto_display || paciente.localidad_nombre || '—'}</Typography>
                            </div>
                        </Paper>
                    )}

                    <Paper className={classes.paper}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                            Historial de órdenes de práctica
                        </Typography>
                        {ordenes.length === 0 ? (
                            <Typography className={classes.emptyOrders}>
                                Este paciente no tiene órdenes de práctica registradas.
                            </Typography>
                        ) : (
                            <TableContainer className={classes.tablePaper}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>ID</strong></TableCell>
                                            <TableCell><strong>Práctica</strong></TableCell>
                                            <TableCell><strong>Fecha</strong></TableCell>
                                            <TableCell><strong>Establecimiento</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ordenes.map((ord) => (
                                            <TableRow key={ord.id}>
                                                <TableCell>{ord.id}</TableCell>
                                                <TableCell>{ord.practica_nombre || '—'}</TableCell>
                                                <TableCell>{ord.fecha ? Formatter.dateBdToUi(ord.fecha) : '—'}</TableCell>
                                                <TableCell>{ord.establecimiento_nombre || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
